import { CommonModule } from '@angular/common';
import { Component, OnInit, ViewChild, inject, signal } from '@angular/core';
import { RouterLink } from '@angular/router';
import { GoogleMapsModule, MapInfoWindow, MapMarker } from '@angular/google-maps';
import { ApiService } from './api.service';
import { WorkforceOperationsNavComponent } from './workforce-operations-nav.component';

@Component({
  selector: 'app-admin-worker-map',
  standalone: true,
  imports: [CommonModule, RouterLink, GoogleMapsModule, WorkforceOperationsNavComponent],
  template: `
    <div class="min-h-full admin-premium-bg">
      <div class="max-w-[1700px] mx-auto p-5">
        <div class="flex flex-col sm:flex-row sm:items-end justify-between gap-3 mb-5">
          <div>
            <p class="text-[9px] font-bold uppercase tracking-[0.16em] text-[#1A6FA6] mb-1">Workforce Operations</p>
            <h1 class="text-xl font-semibold text-slate-950 tracking-tight">Workers Control</h1>
            <p class="text-slate-500 text-xs">Control worker readiness, app login health, live map positions, GPS gaps, and active job context.</p>
          </div>
          <div class="flex flex-wrap items-center gap-2">
            <a routerLink="/admin/dashboard" class="h-9 px-3 rounded-md border border-slate-200 bg-white text-slate-600 text-xs font-bold flex items-center gap-2 hover:bg-slate-50">
              <i class="ti ti-layout-dashboard"></i> Dashboard
            </a>
            <button type="button" (click)="loadMapData()" [disabled]="loading()" class="h-9 px-3 rounded-md bg-[#0C3A5A] text-white text-xs font-bold flex items-center gap-2 hover:bg-[#082d46] disabled:opacity-60">
              <i class="ti" [class.ti-loader]="loading()" [class.animate-spin]="loading()" [class.ti-refresh]="!loading()"></i> Refresh
            </button>
          </div>
        </div>

        <app-workforce-operations-nav active="map"></app-workforce-operations-nav>

        <div class="mb-4 rounded-xl border border-[#E7EEF5] bg-white shadow-sm p-1 flex flex-wrap gap-1">
          @for (tab of controlTabs; track tab.key) {
            <button type="button" (click)="activeControlTab.set(tab.key)" class="h-9 px-3 rounded-lg text-xs font-bold flex items-center gap-2 transition-colors"
                    [ngClass]="activeControlTab() === tab.key ? 'bg-[#0C3A5A] text-white shadow-sm' : 'text-slate-600 hover:bg-slate-50'">
              <i class="ti" [class]="'ti-' + tab.icon"></i>
              {{ tab.label }}
            </button>
          }
        </div>

        <section [class.hidden]="activeControlTab() !== 'app'" class="space-y-4">
          <div class="grid grid-cols-2 lg:grid-cols-5 gap-2.5">
            <div class="bg-white rounded-xl border border-[#E7EEF5] shadow-sm px-3.5 py-2.5">
              <p class="text-[10px] uppercase tracking-widest font-bold text-slate-400">Field workers</p>
              <p class="text-lg font-semibold text-slate-900 mt-0.5">{{ fieldWorkerCount() }}</p>
            </div>
            <div class="bg-white rounded-xl border border-[#E7EEF5] shadow-sm px-3.5 py-2.5">
              <p class="text-[10px] uppercase tracking-widest font-bold text-slate-400">App installed</p>
              <p class="text-lg font-semibold text-emerald-700 mt-0.5">{{ appInstalledCount() }}</p>
            </div>
            <div class="bg-white rounded-xl border border-[#E7EEF5] shadow-sm px-3.5 py-2.5">
              <p class="text-[10px] uppercase tracking-widest font-bold text-slate-400">Browser only</p>
              <p class="text-lg font-semibold text-sky-700 mt-0.5">{{ browserOnlyCount() }}</p>
            </div>
            <div class="bg-white rounded-xl border border-[#E7EEF5] shadow-sm px-3.5 py-2.5">
              <p class="text-[10px] uppercase tracking-widest font-bold text-slate-400">Need setup</p>
              <p class="text-lg font-semibold text-amber-700 mt-0.5">{{ appAttentionCount() }}</p>
            </div>
            <div class="bg-white rounded-xl border border-[#E7EEF5] shadow-sm px-3.5 py-2.5">
              <p class="text-[10px] uppercase tracking-widest font-bold text-slate-400">GPS active</p>
              <p class="text-lg font-semibold text-[#1A6FA6] mt-0.5">{{ locatedWorkerCount() }}</p>
            </div>
          </div>

          <div class="rounded-lg border border-sky-100 bg-sky-50 px-4 py-3">
            <p class="text-[10px] font-bold uppercase tracking-[0.12em] text-[#1A6FA6]">Worker app health</p>
            <p class="text-[12px] font-semibold text-slate-800 mt-0.5">Use this to confirm whether each field worker has activated, completed OTP login, opened the mobile app, and is running it as an installed app.</p>
          </div>

          <div class="bg-white rounded-2xl border border-[#E7EEF5] shadow-sm overflow-hidden">
            <div class="px-4 py-3 border-b border-slate-100 flex items-center justify-between gap-3">
              <div>
                <h2 class="text-[13px] font-bold text-slate-900">Worker readiness</h2>
                <p class="text-[10px] text-slate-500 mt-0.5">Sorted by workers needing setup first.</p>
              </div>
              <button type="button" (click)="loadStaffAppStatus()" class="h-8 px-3 rounded-md bg-white border border-slate-200 text-slate-600 text-[10px] font-bold hover:bg-slate-50">
                <i class="ti ti-refresh"></i> Refresh
              </button>
            </div>
            <div class="divide-y divide-slate-100">
              @for (staff of sortedFieldWorkers(); track staff.id) {
                <div class="px-4 py-3 grid grid-cols-1 lg:grid-cols-[1.4fr_1fr_1fr_1fr] gap-3 items-center">
                  <div class="min-w-0">
                    <p class="text-sm font-bold text-slate-900 truncate">{{ staff.name }}</p>
                    <p class="text-xs text-slate-500 truncate">{{ staff.email }}</p>
                  </div>
                  <div>
                    <span class="inline-flex items-center gap-1.5 px-2 py-1 rounded-md border text-[10px] font-bold" [ngClass]="appHealthBadgeClass(staff)">
                      <i class="ti" [class]="'ti-' + appHealthIcon(staff)"></i>
                      {{ appHealthLabel(staff) }}
                    </span>
                  </div>
                  <div class="text-xs text-slate-600">
                    <p class="font-semibold">{{ staff.last_login_at ? (staff.last_login_at | date:'d MMM y, h:mma') : 'No login recorded' }}</p>
                    <p class="text-[10px] text-slate-400">Last OTP login</p>
                  </div>
                  <div class="text-xs text-slate-600">
                    <p class="font-semibold">{{ staff.app_status?.last_seen_at ? (staff.app_status?.last_seen_at | date:'d MMM y, h:mma') : 'App not seen' }}</p>
                    <p class="text-[10px] text-slate-400">{{ appHealthDetail(staff) }}</p>
                  </div>
                </div>
              } @empty {
                <div class="p-10 text-center">
                  <i class="ti ti-users-off text-4xl text-slate-300"></i>
                  <p class="text-sm font-semibold text-slate-900 mt-3">No field workers found</p>
                  <p class="text-xs text-slate-500 mt-1">Field staff app health will appear once workers are available.</p>
                </div>
              }
            </div>
          </div>
        </section>

        <div [class.hidden]="activeControlTab() !== 'map'">
        <div class="mb-4 rounded-lg border border-sky-100 bg-sky-50 px-4 py-3 flex flex-col lg:flex-row lg:items-center justify-between gap-3">
          <div>
            <p class="text-[10px] font-bold uppercase tracking-[0.12em] text-[#1A6FA6]">Live worker map</p>
            <p class="text-[12px] font-semibold text-slate-800 mt-0.5">Pins show workers and fleet with usable GPS. Missing GPS appears in the attention list.</p>
            <p class="text-[10px] text-slate-500 mt-1">Last refreshed {{ lastRefreshedLabel() }}</p>
          </div>
          <div class="flex flex-wrap gap-1.5">
            @for (filter of mapFilters; track filter.key) {
              <button type="button" (click)="viewFilter.set(filter.key)" class="h-8 px-3 rounded-md border text-[10px] font-bold flex items-center gap-1.5" [ngClass]="viewFilter() === filter.key ? 'bg-[#0C3A5A] border-[#0C3A5A] text-white' : 'bg-white border-slate-200 text-slate-600 hover:text-[#1A6FA6]'">
                <i class="ti" [class]="'ti-' + filter.icon"></i>
                {{ filter.label }}
              </button>
            }
          </div>
        </div>

        <div class="grid grid-cols-2 lg:grid-cols-5 gap-2.5 mb-4">
          <div class="bg-white rounded-xl border border-[#E7EEF5] shadow-sm px-3.5 py-2.5">
            <p class="text-[10px] uppercase tracking-widest font-bold text-slate-400">Active shifts</p>
            <p class="text-lg font-semibold text-slate-900 mt-0.5">{{ workerLocations().length }}</p>
          </div>
          <div class="bg-white rounded-xl border border-[#E7EEF5] shadow-sm px-3.5 py-2.5">
            <p class="text-[10px] uppercase tracking-widest font-bold text-slate-400">GPS visible</p>
            <p class="text-lg font-semibold text-emerald-700 mt-0.5">{{ locatedWorkerCount() }}</p>
          </div>
          <div class="bg-white rounded-xl border border-[#E7EEF5] shadow-sm px-3.5 py-2.5">
            <p class="text-[10px] uppercase tracking-widest font-bold text-slate-400">Missing GPS</p>
            <p class="text-lg font-semibold text-amber-700 mt-0.5">{{ missingLocationCount() }}</p>
          </div>
          <div class="bg-white rounded-xl border border-[#E7EEF5] shadow-sm px-3.5 py-2.5">
            <p class="text-[10px] uppercase tracking-widest font-bold text-slate-400">Fleet pins</p>
            <p class="text-lg font-semibold text-[#1A6FA6] mt-0.5">{{ visibleFleetVehicles().length }}</p>
          </div>
          <div class="bg-white rounded-xl border border-[#E7EEF5] shadow-sm px-3.5 py-2.5">
            <p class="text-[10px] uppercase tracking-widest font-bold text-slate-400">Active jobs</p>
            <p class="text-lg font-semibold text-slate-900 mt-0.5">{{ activeJobCount() }}</p>
          </div>
        </div>

        @if (attentionWorkers().length) {
          <section class="bg-white rounded-xl border border-amber-100 shadow-sm overflow-hidden mb-4">
            <div class="px-4 py-3 border-b border-amber-100 bg-amber-50 flex items-center justify-between gap-3">
              <div>
                <h2 class="text-[12px] font-bold text-amber-900">GPS attention needed</h2>
                <p class="text-[10px] text-amber-700 mt-0.5">These active shifts are missing GPS or have stale location data.</p>
              </div>
              <span class="px-2 py-1 rounded-md bg-white text-amber-700 text-[10px] font-bold">{{ attentionWorkers().length }}</span>
            </div>
            <div class="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-2 p-3">
              @for (loc of attentionWorkers(); track loc.user_id) {
                <div class="rounded-lg border border-amber-100 bg-amber-50/40 px-3 py-2">
                  <div class="flex items-start justify-between gap-2">
                    <div class="min-w-0">
                      <p class="text-[12px] font-bold text-slate-900 truncate">{{ loc.worker_name || 'Active worker' }}</p>
                      <p class="text-[10px] text-slate-500 truncate">{{ loc.rego || loc.vehicle_name || 'No vehicle linked' }}</p>
                    </div>
                    <span class="px-2 py-0.5 rounded-md border text-[9px] font-bold uppercase" [ngClass]="locationBadgeClass(loc)">{{ locationBadgeLabel(loc) }}</span>
                  </div>
                  <p class="text-[10px] text-slate-500 mt-1">Updated {{ formatDateTime(loc.location_updated_at) }}</p>
                  @if (loc.active_jobs?.length) {
                    <a [routerLink]="['/admin/dispatch/job', loc.active_jobs[0].id]" class="mt-2 inline-flex text-[10px] font-bold text-[#1A6FA6] hover:underline">Open active job</a>
                  }
                </div>
              }
            </div>
          </section>
        }

        <section class="bg-white rounded-2xl border border-[#E7EEF5] shadow-sm overflow-hidden">
          @if (!mapsApiKey()) {
            <div class="p-10 text-center">
              <i class="ti ti-map-off text-4xl text-slate-300"></i>
              <h2 class="text-lg font-semibold text-slate-900 mt-3">Google Maps key required</h2>
              <p class="text-sm text-slate-500 mt-1">Add the browser API key in Company Settings to enable the worker and fleet map.</p>
            </div>
          } @else if (!apiLoaded()) {
            <div class="p-10 text-center text-sm font-semibold text-slate-500">
              <i class="ti ti-loader animate-spin mr-2"></i> Loading map...
            </div>
          } @else {
            <google-map height="560px" width="100%" [center]="mapCenter()" [zoom]="mapZoom()" [options]="mapOptions">
              @for (pin of mapPins(); track pin.key) {
                <map-marker #workerMarker="mapMarker" [position]="pin.position" [title]="pin.title" [options]="markerOptions(pin)" (mapClick)="openPin(workerMarker, pin)"></map-marker>
              }
              <map-info-window #workerInfoWindow="mapInfoWindow">
                @if (selectedPin(); as pin) {
                  <div class="min-w-[250px] max-w-[330px] text-[12px] text-slate-700">
                    <p class="font-bold text-slate-950">{{ pin.title }}</p>
                    <p class="mt-1 text-slate-500">{{ pin.subtitle }}</p>
                    <div class="mt-2 flex flex-wrap gap-1">
                      @for (badge of pin.badges || []; track badge) {
                        <span class="px-2 py-0.5 rounded bg-slate-100 text-[10px] font-bold text-slate-600">{{ badge }}</span>
                      }
                    </div>
                    @if (pin.detail) {
                      <p class="mt-2 text-slate-600">{{ pin.detail }}</p>
                    }
                    @if (pin.jobs?.length) {
                      <div class="mt-2 space-y-1">
                        @for (job of pin.jobs; track job.id) {
                          <a [routerLink]="['/admin/dispatch/job', job.id]" class="block rounded border border-slate-100 bg-slate-50 px-2 py-1 hover:bg-sky-50">
                            <p class="font-bold text-slate-800">#{{ job.id }} {{ job.status }}</p>
                            <p class="text-slate-500">{{ job.address || job.customer_name || 'Active job' }}</p>
                          </a>
                        }
                      </div>
                    }
                  </div>
                }
              </map-info-window>
            </google-map>
            <div class="px-4 py-2 border-t border-slate-100 bg-white flex flex-wrap gap-2">
              @for (item of workerLegend; track item.label) {
                <span class="inline-flex items-center gap-1.5 text-[10px] font-bold text-slate-500">
                  <span class="w-2.5 h-2.5 rounded-full" [style.background]="item.color"></span>{{ item.label }}
                </span>
              }
            </div>
          }
        </section>

        <div class="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-3 mt-4">
          @for (loc of visibleWorkerLocations(); track loc.user_id) {
            <div class="rounded-xl border border-slate-200 bg-white p-3">
              <div class="flex items-start justify-between gap-3">
                <div class="min-w-0">
                  <p class="text-sm font-semibold text-slate-900 truncate">{{ loc.worker_name || 'Active worker' }}</p>
                  <p class="text-xs text-slate-500 truncate">{{ loc.rego || loc.vehicle_name || 'No vehicle linked' }}</p>
                </div>
                <span class="px-2 py-1 rounded-md border text-[10px] font-bold uppercase" [ngClass]="locationBadgeClass(loc)">{{ locationBadgeLabel(loc) }}</span>
              </div>
              <p class="text-xs text-slate-600 mt-2 truncate">{{ loc.current_job || loc.vehicle_location || 'On active shift' }}</p>
              @if (loc.active_jobs?.length) {
                <div class="mt-2 rounded-lg bg-sky-50 border border-sky-100 px-2 py-1.5">
                  <p class="text-[10px] font-bold text-[#1A6FA6] uppercase">Active job</p>
                  <a [routerLink]="['/admin/dispatch/job', loc.active_jobs[0].id]" class="text-xs font-semibold text-slate-800 truncate block hover:text-[#1A6FA6]">#{{ loc.active_jobs[0].id }} {{ loc.active_jobs[0].address || loc.active_jobs[0].customer_name }}</a>
                </div>
              }
              <p class="text-[10px] text-slate-400 mt-2">Updated {{ formatDateTime(loc.location_updated_at) }}</p>
            </div>
          } @empty {
            <div class="xl:col-span-3 rounded-2xl border border-slate-200 bg-white p-10 text-center">
              <i class="ti ti-map-pin-off text-4xl text-slate-300"></i>
              <p class="text-sm font-semibold text-slate-900 mt-3">No workers in this view</p>
              <p class="text-xs text-slate-500 mt-1">Try another map filter or refresh the worker locations.</p>
            </div>
          }
        </div>
        </div>
      </div>
    </div>
  `
})
export class AdminWorkerMapComponent implements OnInit {
  @ViewChild('workerInfoWindow') workerInfoWindow?: MapInfoWindow;

  workerLocations = signal<any[]>([]);
  fleetVehicles = signal<any[]>([]);
  staffList = signal<any[]>([]);
  selectedPin = signal<any | null>(null);
  apiLoaded = signal(false);
  mapsApiKey = signal('');
  loading = signal(false);
  activeControlTab = signal<'app' | 'map'>('app');
  viewFilter = signal<'all' | 'workers' | 'gps-issues' | 'on-job' | 'fleet'>('all');
  lastRefreshedAt = signal<Date | null>(null);
  controlTabs: Array<{ key: 'app' | 'map'; label: string; icon: string }> = [
    { key: 'app', label: 'App Health', icon: 'device-mobile-check' },
    { key: 'map', label: 'Workers Map', icon: 'map-2' }
  ];
  workerLegend = [
    { label: 'Worker on active job', color: '#2563eb' },
    { label: 'Live worker', color: '#059669' },
    { label: 'Stale GPS', color: '#d97706' },
    { label: 'Fleet on job', color: '#7c3aed' },
    { label: 'Assigned fleet', color: '#0f766e' },
    { label: 'Unassigned fleet', color: '#64748b' }
  ];
  mapFilters: Array<{ key: 'all' | 'workers' | 'gps-issues' | 'on-job' | 'fleet'; label: string; icon: string }> = [
    { key: 'all', label: 'All', icon: 'map-pin' },
    { key: 'workers', label: 'Workers', icon: 'user-pin' },
    { key: 'gps-issues', label: 'GPS issues', icon: 'map-pin-off' },
    { key: 'on-job', label: 'On job', icon: 'tool' },
    { key: 'fleet', label: 'Fleet', icon: 'truck' }
  ];

  mapOptions: google.maps.MapOptions = {
    mapTypeControl: false,
    streetViewControl: false,
    fullscreenControl: true
  };

  private api = inject(ApiService);

  ngOnInit() {
    this.loadMapSettings();
    this.loadMapData();
    this.loadStaffAppStatus();
  }

  loadMapData() {
    this.loading.set(true);
    this.api.getWorkerMapLocations().subscribe({
      next: (res: any) => {
        this.loading.set(false);
        if (res.success) {
          this.workerLocations.set(res.data?.locations || []);
          this.fleetVehicles.set(res.data?.vehicles || []);
          this.lastRefreshedAt.set(new Date());
        }
      },
      error: () => {
        this.loading.set(false);
        this.workerLocations.set([]);
        this.fleetVehicles.set([]);
      }
    });
  }

  loadMapSettings() {
    this.api.getCompanySettings().subscribe({
      next: (res: any) => {
        const key = String(res?.data?.google_maps_browser_api_key || '').trim();
        this.mapsApiKey.set(key);
        if (key) this.loadGoogleMapsScript(key);
      }
    });
  }

  loadStaffAppStatus() {
    this.api.getStaff().subscribe({
      next: (res: any) => {
        const users = res.success && res.data?.users ? res.data.users : [];
        this.staffList.set(users.map((u: any) => ({
          ...u,
          can_work_as_worker: !!Number(u.can_work_as_worker || u.has_worker_access),
          has_worker_access: !!Number(u.can_work_as_worker || u.has_worker_access) || u.role === 'Worker',
          is_activated: !!Number(u.is_activated),
          is_active: !!Number(u.is_active),
          app_status: u.app_status || null
        })));
      },
      error: () => this.staffList.set([])
    });
  }

  fieldWorkers(): any[] {
    return this.staffList().filter((staff: any) => this.hasFieldStaffAccess(staff));
  }

  sortedFieldWorkers(): any[] {
    return [...this.fieldWorkers()].sort((a: any, b: any) => this.appHealthPriority(b) - this.appHealthPriority(a));
  }

  fieldWorkerCount(): number {
    return this.fieldWorkers().length;
  }

  appInstalledCount(): number {
    return this.fieldWorkers().filter((staff: any) => !!staff.app_status?.is_installed).length;
  }

  browserOnlyCount(): number {
    return this.fieldWorkers().filter((staff: any) => staff.app_status?.status === 'browser_only').length;
  }

  appAttentionCount(): number {
    return this.fieldWorkers().filter((staff: any) => this.needsAppAttention(staff)).length;
  }

  hasFieldStaffAccess(staff: any): boolean {
    return staff?.role === 'Worker' || !!staff?.can_work_as_worker || !!staff?.has_worker_access;
  }

  appHealthLabel(staff: any): string {
    return staff?.app_status?.label || (staff?.last_login_at ? 'Logged in, app not seen' : 'No app login');
  }

  appHealthDetail(staff: any): string {
    const status = staff?.app_status;
    if (!staff?.is_activated) return 'Waiting for account activation';
    if (!staff?.last_login_at) return 'OTP login not completed';
    if (!status?.last_seen_at) return 'No app heartbeat received';
    if (status?.display_mode === 'standalone') return 'Installed app mode detected';
    return status?.platform || status?.display_mode || 'Mobile app heartbeat received';
  }

  appHealthIcon(staff: any): string {
    const status = String(staff?.app_status?.status || '');
    if (status === 'installed') return 'device-mobile-check';
    if (status === 'browser_only') return 'world';
    if (status === 'installed_inactive') return 'device-mobile-question';
    if (status === 'activated_never_logged_in' || status === 'logged_in_no_app_ping') return 'alert-circle';
    return 'device-mobile';
  }

  appHealthBadgeClass(staff: any): string {
    const status = String(staff?.app_status?.status || '');
    if (status === 'installed') return 'bg-emerald-50 text-emerald-700 border-emerald-100';
    if (status === 'browser_only') return 'bg-sky-50 text-sky-700 border-sky-100';
    if (status === 'installed_inactive') return 'bg-amber-50 text-amber-700 border-amber-100';
    if (status === 'activated_never_logged_in' || status === 'logged_in_no_app_ping') return 'bg-rose-50 text-rose-700 border-rose-100';
    return 'bg-slate-50 text-slate-600 border-slate-100';
  }

  needsAppAttention(staff: any): boolean {
    const status = String(staff?.app_status?.status || '');
    return ['invited', 'activated_never_logged_in', 'logged_in_no_app_ping', 'browser_only', 'installed_inactive'].includes(status);
  }

  private appHealthPriority(staff: any): number {
    const status = String(staff?.app_status?.status || '');
    if (status === 'activated_never_logged_in' || status === 'logged_in_no_app_ping') return 5;
    if (status === 'invited') return 4;
    if (status === 'browser_only') return 3;
    if (status === 'installed_inactive') return 2;
    return 1;
  }

  mapPins(): any[] {
    const pins: any[] = [];
    const activeAssignedUserIds = new Set<number>();

    this.visibleWorkerLocations().forEach((loc: any) => {
      if (!this.hasCoordinates(loc)) return;
      const userId = Number(loc.user_id || 0);
      if (userId > 0) activeAssignedUserIds.add(userId);
      pins.push({
        key: `worker-${loc.user_id}`,
        kind: 'worker',
        color: this.workerMarkerColor(loc),
        title: loc.worker_name || 'Active worker',
        subtitle: loc.rego || loc.vehicle_name ? `${loc.rego || loc.vehicle_name} - ${this.locationBadgeLabel(loc)}` : this.locationBadgeLabel(loc),
        detail: loc.current_job || loc.vehicle_location || 'On active shift',
        badges: [this.workerMarkerTag(loc), 'Worker', loc.rego || loc.vehicle_name || 'No vehicle', `${loc.active_job_count || 0} active job${Number(loc.active_job_count || 0) === 1 ? '' : 's'}`].filter(Boolean),
        jobs: loc.active_jobs || [],
        position: { lat: Number(loc.latitude), lng: Number(loc.longitude) }
      });
    });

    this.visibleFleetVehicles().forEach((vehicle: any) => {
      if (!this.hasCoordinates(vehicle)) return;
      const assignedUserId = Number(vehicle.assigned_user_id || 0);
      if (assignedUserId > 0 && activeAssignedUserIds.has(assignedUserId)) return;
      pins.push({
        key: `vehicle-${vehicle.id}`,
        kind: 'vehicle',
        color: this.vehicleMarkerColor(vehicle),
        title: vehicle.vehicle || vehicle.rego || 'Fleet vehicle',
        subtitle: vehicle.rego ? `${vehicle.rego} - ${vehicle.status || 'Vehicle'}` : (vehicle.status || 'Vehicle'),
        detail: vehicle.job && vehicle.job !== '-' ? vehicle.job : (vehicle.location || 'No current job recorded'),
        badges: [this.vehicleMarkerTag(vehicle), 'Fleet', vehicle.name || 'Unassigned', vehicle.status || 'Available'].filter(Boolean),
        jobs: [],
        position: { lat: Number(vehicle.latitude), lng: Number(vehicle.longitude) }
      });
    });

    return pins;
  }

  mapCenter(): google.maps.LatLngLiteral {
    const first = this.mapPins()[0];
    return first ? first.position : { lat: -36.8485, lng: 174.7633 };
  }

  mapZoom(): number {
    return this.mapPins().length > 1 ? 10 : 12;
  }

  markerOptions(pin: any): google.maps.MarkerOptions {
    return {
      icon: this.mapPinIcon(pin.color || '#475569'),
      label: {
        text: pin.kind === 'worker' ? 'W' : 'V',
        color: '#ffffff',
        fontWeight: '700',
        fontSize: '11px'
      }
    };
  }

  visibleWorkerLocations(): any[] {
    const filter = this.viewFilter();
    if (filter === 'fleet') return [];
    if (filter === 'gps-issues') return this.attentionWorkers();
    if (filter === 'on-job') return this.workerLocations().filter((loc: any) => Number(loc.active_job_count || 0) > 0);
    return this.workerLocations();
  }

  visibleFleetVehicles(): any[] {
    const filter = this.viewFilter();
    if (filter === 'workers' || filter === 'gps-issues' || filter === 'on-job') return [];
    return this.fleetVehicles().filter((vehicle: any) => this.hasCoordinates(vehicle));
  }

  attentionWorkers(): any[] {
    return this.workerLocations().filter((loc: any) => this.locationBadgeLabel(loc) !== 'Live');
  }

  openPin(marker: MapMarker, pin: any) {
    this.selectedPin.set(pin);
    this.workerInfoWindow?.open(marker);
  }

  private workerMarkerColor(loc: any): string {
    if (Number(loc?.active_job_count || 0) > 0) return '#2563eb';
    if (this.locationBadgeLabel(loc) === 'Live') return '#059669';
    if (this.locationBadgeLabel(loc) === 'Stale') return '#d97706';
    return '#dc2626';
  }

  private workerMarkerTag(loc: any): string {
    if (Number(loc?.active_job_count || 0) > 0) return 'Blue: active job';
    if (this.locationBadgeLabel(loc) === 'Live') return 'Green: live worker';
    if (this.locationBadgeLabel(loc) === 'Stale') return 'Amber: stale GPS';
    return 'Red: missing GPS';
  }

  private vehicleMarkerColor(vehicle: any): string {
    if (!vehicle?.assigned_user_id) return '#64748b';
    const status = String(vehicle?.status || '').toLowerCase();
    if (status.includes('maintenance') || status.includes('service')) return '#ea580c';
    if (status.includes('unavailable') || status.includes('off')) return '#dc2626';
    if (vehicle?.job && vehicle.job !== '-') return '#7c3aed';
    return '#0f766e';
  }

  private vehicleMarkerTag(vehicle: any): string {
    if (!vehicle?.assigned_user_id) return 'Grey: unassigned fleet';
    const status = String(vehicle?.status || '').toLowerCase();
    if (status.includes('maintenance') || status.includes('service')) return 'Orange: maintenance';
    if (status.includes('unavailable') || status.includes('off')) return 'Red: unavailable';
    if (vehicle?.job && vehicle.job !== '-') return 'Purple: fleet on job';
    return 'Teal: assigned fleet';
  }

  private mapPinIcon(color: string): google.maps.Icon {
    const svg = `<svg xmlns="http://www.w3.org/2000/svg" width="42" height="50" viewBox="0 0 42 50"><path fill="${color}" stroke="#ffffff" stroke-width="2" d="M21 2C11.1 2 3 10.1 3 20c0 13.5 18 28 18 28s18-14.5 18-28C39 10.1 30.9 2 21 2Z"/><circle cx="21" cy="20" r="9" fill="rgba(255,255,255,0.22)"/></svg>`;
    return {
      url: `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(svg)}`,
      scaledSize: new google.maps.Size(34, 40),
      labelOrigin: new google.maps.Point(17, 18),
      anchor: new google.maps.Point(17, 40)
    };
  }

  locatedWorkerCount(): number {
    return this.workerLocations().filter((loc: any) => this.hasCoordinates(loc)).length;
  }

  missingLocationCount(): number {
    return this.workerLocations().filter((loc: any) => !this.hasCoordinates(loc)).length;
  }

  vehiclePinCount(): number {
    return this.mapPins().filter((pin: any) => pin.kind === 'vehicle').length;
  }

  activeJobCount(): number {
    return this.workerLocations().reduce((sum: number, loc: any) => sum + Number(loc.active_job_count || 0), 0);
  }

  hasCoordinates(item: any): boolean {
    return item?.latitude !== null
      && item?.latitude !== undefined
      && item?.latitude !== ''
      && item?.longitude !== null
      && item?.longitude !== undefined
      && item?.longitude !== ''
      && Number.isFinite(Number(item.latitude))
      && Number.isFinite(Number(item.longitude));
  }

  locationBadgeLabel(loc: any): string {
    if (!this.hasCoordinates(loc)) return 'Missing';
    if (this.isLocationStale(loc.location_updated_at)) return 'Stale';
    return 'Live';
  }

  locationBadgeClass(loc: any): string {
    const label = this.locationBadgeLabel(loc);
    if (label === 'Live') return 'bg-emerald-50 text-emerald-700 border-emerald-100';
    if (label === 'Stale') return 'bg-amber-50 text-amber-700 border-amber-100';
    return 'bg-rose-50 text-rose-700 border-rose-100';
  }

  formatDateTime(value: string | null): string {
    if (!value) return 'not recorded';
    const date = this.parseApiDate(value);
    if (Number.isNaN(date.getTime())) return 'not recorded';
    return date.toLocaleString('en-NZ', { day: 'numeric', month: 'short', hour: 'numeric', minute: '2-digit', timeZone: 'Pacific/Auckland', timeZoneName: 'short' });
  }

  lastRefreshedLabel(): string {
    const value = this.lastRefreshedAt();
    if (!value) return 'not yet';
    return value.toLocaleTimeString('en-NZ', { hour: 'numeric', minute: '2-digit', timeZone: 'Pacific/Auckland' });
  }

  private isLocationStale(value: string | null): boolean {
    if (!value) return true;
    const date = this.parseApiDate(value);
    if (Number.isNaN(date.getTime())) return true;
    return Date.now() - date.getTime() > 10 * 60 * 1000;
  }

  private parseApiDate(value: string): Date {
    const normalized = /^\d{4}-\d{2}-\d{2} \d{2}:\d{2}:\d{2}$/.test(value)
      ? `${value.replace(' ', 'T')}Z`
      : value;
    return new Date(normalized);
  }

  private loadGoogleMapsScript(key: string) {
    if (typeof window === 'undefined') return;
    if ((window as any).google?.maps) {
      this.apiLoaded.set(true);
      return;
    }
    const existing = document.getElementById('google-maps-js');
    if (existing) {
      existing.addEventListener('load', () => this.apiLoaded.set(true));
      return;
    }
    const script = document.createElement('script');
    script.id = 'google-maps-js';
    script.src = `https://maps.googleapis.com/maps/api/js?key=${encodeURIComponent(key)}&libraries=places`;
    script.async = true;
    script.defer = true;
    script.onload = () => this.apiLoaded.set(true);
    document.head.appendChild(script);
  }
}
